import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { axiosInstance, getCachedData, getApiErrorMessage } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Calendar, FileText, Clock, User, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import DataExportButton from "../components/common/DataExportButton";
import { PageLayout } from "../components/layout/Sidebar";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import { useModuleAccess } from "../hooks/useModuleAccess";
import { DailyLogCard, DailyLogForm } from "../components/dailylogs";
import { optimizeCloudinaryUrl } from "../components/common/LazyImage";
import { FloatingElements, AnimatedHeader, AnimatedCard } from "../components/ui/AnimatedElements";
import CalendarWithDots from "../components/common/CalendarWithDots";
import { getLocalDateString } from "../lib/utils";
import { confirmIfBeforeNoon } from "../lib/timeUtils";
import AlphabetFilter from "../components/AlphabetFilter";
import StatisticsModal, { StatisticsSummary } from "../components/StatisticsModal";

// Helper function to download image
const downloadImage = async (imageUrl, filename = "gambar") => {
  try {
    toast.loading("Memuat turun gambar...", { id: "download" });
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${getLocalDateString()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Gambar berjaya dimuat turun!", { id: "download" });
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Gagal memuat turun gambar", { id: "download" });
  }
};

export default function DailyLogsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { isModuleOn } = useModuleAccess(user?.role);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [children, setChildren] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [selectedClass, setSelectedClass] = useState("all");
  const [nameFilter, setNameFilter] = useState(""); // NEW: For alphabet filter
  const [showStatsModal, setShowStatsModal] = useState(false); // NEW: For statistics modal
  const [statsData, setStatsData] = useState(null); // NEW: Statistics data
  const [viewImageUrl, setViewImageUrl] = useState(null);
  const [addMode, setAddMode] = useState("individual");
  const [selectedClassForBulk, setSelectedClassForBulk] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [excludedChildIds, setExcludedChildIds] = useState([]); // New: for excluding children
  const [excludeSearch, setExcludeSearch] = useState(""); // New: search for exclusion
  const [highlightedLogId, setHighlightedLogId] = useState(null); // For deep link highlighting
  const [activeFormTab, setActiveFormTab] = useState("saringan"); // "saringan", "aktiviti", "kesihatan"
  const highlightedCardRef = useRef(null);
  
  // State untuk Duplicate Feature
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateLog, setDuplicateLog] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState("individual");
  const [selectedChildrenForDuplicate, setSelectedChildrenForDuplicate] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  
  // Handle deep link from notifications
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const highlightParam = searchParams.get('highlight');
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
    if (highlightParam) {
      setHighlightedLogId(highlightParam);
      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedLogId(null), 5000);
      // Clear the search params after processing
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Save selectedClass to localStorage for StatisticsSummary component
  useEffect(() => {
    localStorage.setItem('selectedClass', selectedClass);
  }, [selectedClass]);
  
  // Scroll to highlighted card when it's set
  useEffect(() => {
    if (highlightedLogId && highlightedCardRef.current) {
      // Wait for DOM to update
      setTimeout(() => {
        highlightedCardRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 500);
    }
  }, [highlightedLogId, dailyLogs]);
  
  const getDefaultFormData = () => ({
    child_id: "",
    attendance: "Hadir",
    mood: "",
    notes: "",
    date: getLocalDateString(),
    // SUSU (Milk)
    susu: {
      times: [],
      quantity_oz: "",
      frequency: "",
      notes: ""
    },
    // Tukar Lampin/Tandas
    diaper_change: {
      times: [],
      frequency: "",
      stool_type: "",
      stool_color: "",
      notes: ""
    },
    // Gosok Gigi
    brush_teeth: {
      morning: false,
      afternoon: false,
      evening: false
    },
    // Mandi
    bath: {
      morning: false,
      evening: false
    },
    // Kesihatan
    health_notes: "",
    health_photo_url: "",
    // STATUS & SARINGAN KANAK-KANAK
    status_saringan: {
      waktu_tiba: {
        hour: "",
        minute: "",
        period: "AM"
      },
      penghantar: {
        ibu_bapa: false,
        keluarga: false,
        penjaga: false,
        perkhidmatan: false,
        lain_lain: false,
        lain_lain_text: ""
      },
      tidur_nyenyak: {
        status: "",
        sebab_demam: false,
        sebab_selesema: false,
        sebab_batuk: false,
        sebab_lain: false,
        sebab_lain_text: ""
      },
      status_kesihatan: {
        sihat: "",
        suhu_badan: ""
      },
      sudah_mandi: "",
      alatan_dalam_beg: {
        botol_susu: "",
        lampin: "",
        botol_air: "",
        tuala_mandi: "",
        pakaian_bersih: "",
        bekas_baju_kotor: ""
      },
      ubat_dibawa: {
        demam: { jenis: "demam", ada: false, waktu_terakhir_hour: "", waktu_terakhir_period: "AM", waktu_seterusnya_hour: "", waktu_seterusnya_period: "AM" },
        selesema: { jenis: "selesema", ada: false, waktu_terakhir_hour: "", waktu_terakhir_period: "AM", waktu_seterusnya_hour: "", waktu_seterusnya_period: "AM" },
        batuk: { jenis: "batuk", ada: false, waktu_terakhir_hour: "", waktu_terakhir_period: "AM", waktu_seterusnya_hour: "", waktu_seterusnya_period: "AM" },
        lain_lain: { jenis: "", ada: false, waktu_terakhir_hour: "", waktu_terakhir_period: "AM", waktu_seterusnya_hour: "", waktu_seterusnya_period: "AM" }
      },
      keadaan_fizikal: {
        marks_depan: [],
        marks_belakang: [],
        catatan: ""
      }
    },
    // Ubat Diberi (New medicine tracking)
    ubat_diberi: {
      demam: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' },
      batuk: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' },
      selsema: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' },
      antibiotik: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' },
      ubat_titik: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' },
      ubat_sapu: { selected: false, hour: '', minute: '00', period: 'AM', notes: '' }
    },
    // Sleep tracking
    sleep: {
      count: '',
      start_time: '',
      until_time: '',
      sessions: [],
      notes: ''
    },
    // Injury/Illness tracking
    injury: {
      description: '',
      location: '',
      time_detected: '',
      origin: '',
      notes: ''
    }
  });

  const [formData, setFormData] = useState(getDefaultFormData());
  const [photoFile, setPhotoFile] = useState(null);
  const [healthPhotoFile, setHealthPhotoFile] = useState(null);
  const [stoolPhotoFile, setStoolPhotoFile] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/children');
      let childrenData = response.data;
      
      // Filter by user's branch (except superadmin who can see all)
      if (user?.branch && user?.role !== 'superadmin') {
        childrenData = childrenData.filter(c => c.branch === user.branch);
      }
      
      setChildren(childrenData);
      // Extract unique class names from actual children data
      const uniqueClasses = [...new Set(childrenData.map(c => c.class_name).filter(Boolean))];
      setAvailableClasses(uniqueClasses.sort());
    } catch (error) {
      toast.error("Ralat mendapatkan data kanak-kanak");
    }
  }, [user?.branch, user?.role]);

  const fetchDailyLogs = useCallback(async () => {
    try {
      // Use enhanced endpoint with name filter
      let url = `/daily-logs/enhanced?date=${selectedDate}`;
      if (nameFilter) {
        url += `&name_filter=${encodeURIComponent(nameFilter)}`;
      }
      if (selectedClass && selectedClass !== 'all') {
        url += `&class_name=${encodeURIComponent(selectedClass)}`;
      }
      const response = await axiosInstance.get(url);
      setDailyLogs(response.data);
    } catch (error) {
      toast.error("Ralat mendapatkan log harian");
    }
  }, [selectedDate, nameFilter, selectedClass]);

  useEffect(() => {
    fetchChildren();
    fetchDailyLogs();
  }, [fetchChildren, fetchDailyLogs]);

  // Use useMemo instead of useEffect + setState for filtering
  const filteredChildren = useMemo(() => {
    if (selectedClass === "all") {
      return children;
    }
    return children.filter(c => c.class_name === selectedClass);
  }, [selectedClass, children]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Warning if submitting before 12 PM
    if (!confirmIfBeforeNoon()) return;
    
    // Jenis najis & nota mandatory when diaper times selected
    const diaperTimes = formData.diaper_change?.times || [];
    if (diaperTimes.length > 0) {
      if (!formData.diaper_change?.stool_type) {
        toast.error("Sila pilih Jenis Najis sebelum hantar log");
        return;
      }
      if (!formData.diaper_change?.notes?.trim()) {
        toast.error("Sila isi nota najis sebelum hantar log");
        return;
      }
    }
    
    // Bulk add for entire class or all classes
    if (addMode === "class" && (selectedClassForBulk || selectedClassForBulk === "all") && !editingLog) {
      setBulkSubmitting(true);
      
      // Get children based on class selection
      let classChildren;
      if (selectedClassForBulk === "all") {
        classChildren = children;
      } else {
        classChildren = children.filter(c => c.class_name === selectedClassForBulk);
      }
      
      // Apply exclusions
      classChildren = classChildren.filter(c => !excludedChildIds.includes(c.id));
      
      if (classChildren.length === 0) {
        toast.error("Tiada kanak-kanak untuk ditambah log (semua dikecualikan atau kelas kosong)");
        setBulkSubmitting(false);
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      let dupCount = 0;
      let lastErrorMsg = "";
      
      for (const child of classChildren) {
        try {
          await axiosInstance.post('/daily-logs', {
            ...formData,
            child_id: child.id
          });
          successCount++;
        } catch (error) {
          if (error.response?.status === 409 && error.response?.data?.detail?.is_duplicate_warning) {
            dupCount++;
          } else {
            failCount++;
            lastErrorMsg = getApiErrorMessage(error, "Ralat menyimpan log");
          }
        }
      }
      
      setBulkSubmitting(false);
      
      const classLabel = selectedClassForBulk === "all" ? "semua kelas" : selectedClassForBulk;
      if (successCount > 0) {
        toast.success(`${successCount} log harian berjaya ditambah untuk ${classLabel}!`);
      }
      if (dupCount > 0) {
        toast.warning(`${dupCount} log dilangkau (sudah direkodkan dalam 5 minit yang lepas)`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} log gagal ditambah${lastErrorMsg ? `: ${lastErrorMsg}` : ""}`);
      }
      
      setShowDialog(false);
      setFormData(getDefaultFormData());
      setAddMode("individual");
      setSelectedClassForBulk("");
      setExcludedChildIds([]);
      setExcludeSearch("");
      fetchDailyLogs();
      return;
    }
    
    // Individual add/edit
    if (!formData.child_id && !editingLog) {
      toast.error("Sila pilih kanak-kanak dahulu");
      return;
    }
    try {
      let response;
      if (editingLog) {
        response = await axiosInstance.put(`/daily-logs/${editingLog.id}`, formData);
        toast.success("Log harian berjaya dikemaskini!");
      } else {
        try {
          response = await axiosInstance.post('/daily-logs', formData);
        } catch (postErr) {
          // 409 = amaran duplikat (log sama dalam 5 minit) - backend hantar detail sebagai objek
          const dup = postErr.response?.status === 409 ? postErr.response?.data?.detail : null;
          if (dup && typeof dup === 'object' && dup.is_duplicate_warning) {
            if (!window.confirm(dup.message || "Log serupa sudah direkodkan. Teruskan?")) return;
            response = await axiosInstance.post('/daily-logs?force=true', formData);
          } else {
            throw postErr;
          }
        }
        toast.success("Log harian berjaya ditambah!");
      }
      
      // Upload photo if selected
      if (photoFile && response.data.id) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        await axiosInstance.post(`/daily-logs/${response.data.id}/photo`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      // Upload health photo if selected
      if (healthPhotoFile && response.data.id) {
        const healthPhotoFormData = new FormData();
        healthPhotoFormData.append('file', healthPhotoFile);
        await axiosInstance.post(`/daily-logs/${response.data.id}/health-photo`, healthPhotoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      // Upload stool photo if selected
      if (stoolPhotoFile && response.data.id) {
        const stoolPhotoFormData = new FormData();
        stoolPhotoFormData.append('file', stoolPhotoFile);
        await axiosInstance.post(`/daily-logs/${response.data.id}/stool-photo`, stoolPhotoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowDialog(false);
      setEditingLog(null);
      setFormData(getDefaultFormData());
      setPhotoFile(null);
      setHealthPhotoFile(null);
      setStoolPhotoFile(null);
      fetchDailyLogs();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Ralat menyimpan log"));
    }
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    setFormData({
      child_id: log.child_id,
      attendance: log.attendance,
      mood: log.mood,
      notes: log.notes,
      date: log.date,
      susu: log.susu || getDefaultFormData().susu,
      diaper_change: log.diaper_change || getDefaultFormData().diaper_change,
      brush_teeth: log.brush_teeth || getDefaultFormData().brush_teeth,
      bath: log.bath || getDefaultFormData().bath,
      health_notes: log.health_notes || "",
      health_photo_url: log.health_photo_url || "",
      status_saringan: log.status_saringan || getDefaultFormData().status_saringan,
      ubat_diberi: log.ubat_diberi || getDefaultFormData().ubat_diberi,
      sleep: log.sleep || getDefaultFormData().sleep,
      injury: log.injury || getDefaultFormData().injury
    });
    setShowDialog(true);
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Adakah anda pasti mahu memadam log harian ini?")) return;
    
    try {
      await axiosInstance.delete(`/daily-logs/${logId}`);
      toast.success("Log harian berjaya dipadam!");
      fetchDailyLogs();
    } catch (error) {
      toast.error("Ralat memadam log harian");
    }
  };

  // Handle Duplicate Log
  const handleOpenDuplicate = (log) => {
    setDuplicateLog(log);
    setDuplicateMode("individual");
    setSelectedChildrenForDuplicate([]);
    setShowDuplicateDialog(true);
  };

  const handleDuplicateLog = async () => {
    if (!duplicateLog) return;
    
    setDuplicating(true);
    try {
      const childIds = duplicateMode === "individual" 
        ? [duplicateLog.child_id]
        : selectedChildrenForDuplicate;
      
      if (childIds.length === 0) {
        toast.error("Sila pilih sekurang-kurangnya satu kanak-kanak");
        setDuplicating(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const childId of childIds) {
        try {
          await axiosInstance.post('/daily-logs?force=true', {
            child_id: childId,
            date: duplicateLog.date,
            arrival_time: duplicateLog.arrival_time || "",
            departure_time: duplicateLog.departure_time || "",
            attendance: duplicateLog.attendance || "",
            mood: duplicateLog.mood || "",
            sleep_quality: duplicateLog.sleep_quality || "",
            nap_start: duplicateLog.nap_start || "",
            nap_end: duplicateLog.nap_end || "",
            diaper_changes: duplicateLog.diaper_changes || 0,
            bathroom_visits: duplicateLog.bathroom_visits || 0,
            notes: duplicateLog.notes || "",
            // Complex nested fields
            susu: duplicateLog.susu || null,
            diaper_change: duplicateLog.diaper_change || null,
            brush_teeth: duplicateLog.brush_teeth || null,
            bath: duplicateLog.bath || null,
            sleep: duplicateLog.sleep || null,
            health_notes: duplicateLog.health_notes || "",
            health_photo_url: duplicateLog.health_photo_url || "",
            photo_url: duplicateLog.photo_url || "",
            status_saringan: duplicateLog.status_saringan || null,
            ubat_diberi: duplicateLog.ubat_diberi || null,
            injury: duplicateLog.injury || null
          });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to duplicate for child ${childId}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Berjaya duplicate ${successCount} log harian!`);
        fetchDailyLogs();
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} log harian gagal di-duplicate`);
      }

      setShowDuplicateDialog(false);
      setDuplicateLog(null);
      setSelectedChildrenForDuplicate([]);
    } catch (error) {
      toast.error("Ralat semasa duplicate log harian");
    } finally {
      setDuplicating(false);
    }
  };

  const toggleChildForDuplicate = (childId) => {
    setSelectedChildrenForDuplicate(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const selectAllChildrenForDuplicate = (className) => {
    const classChildren = children.filter(c => c.class_name === className);
    const classChildIds = classChildren.map(c => c.id);
    setSelectedChildrenForDuplicate(prev => {
      const newSelection = [...prev];
      classChildIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const moods = [
    "Sihat",
    "Sakit",
    "Mengantuk",
    "Sedih"
  ];

  const getMoodEmoji = (mood) => {
    const emojiMap = {
      "sihat": "🥰",
      "Sihat": "🥰",
      "sakit": "🤒",
      "Sakit": "🤒",
      "mengantuk": "😴",
      "Mengantuk": "😴",
      "sedih": "😢",
      "Sedih": "😢",
      // Legacy support
      "happy": "🥰",
      "Gembira": "🥰",
      "sad": "😢",
      "tired": "😴",
      "neutral": "😐",
      "excited": "🥰"
    };
    return emojiMap[mood] || "🥰";
  };

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <AnimatedHeader 
            emoji="📋"
            title="Log Harian"
            subtitle="📝 Rekod aktiviti harian kanak-kanak"
            delay={100}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {(user.role === 'admin' || user.role === 'guru') && (
              <DataExportButton 
                dataType="daily-logs"
                childrenList={children}
                classes={availableClasses}
                title="Export"
              />
            )}
            {(user.role === 'admin' || user.role === 'guru') && (
              <Button
                data-testid="add-log-button"
                onClick={() => setShowDialog(true)}
                className="bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white rounded-2xl font-black shadow-lg hover:shadow-xl hover:scale-105 transition-all text-sm px-3 py-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah
              </Button>
            )}
          </div>
        </div>

        {/* Date Filter - Space Explorer Style */}
        <AnimatedCard className="mb-6" delay={200} color="blue">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow-lg">
              <span className="text-2xl">📅</span>
            </div>
            <div className="flex-1">
              <label className="text-sm font-black text-gray-600 mb-1 block">Pilih Tarikh</label>
              <div className="flex items-center gap-3">
                <div 
                  data-testid="date-display"
                  className="flex-1 px-4 py-3 border-3 border-blue-200 rounded-2xl bg-white font-bold text-gray-700"
                >
                  {new Date(selectedDate).toLocaleDateString('ms-MY', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
                <CalendarWithDots 
                  dataType="daily-logs"
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  dotColor="bg-blue-500"
                  user={user}
                />
              </div>
            </div>
          </div>
        </AnimatedCard>

        {/* Class Filter - Space Explorer Style */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && availableClasses.length > 0 && isModuleOn("filter_class_daily_logs") && (
          <AnimatedCard className="mb-6" delay={300} color="purple">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
                <span className="text-xl">🏫</span>
              </div>
              <h3 className="font-black text-gray-700">Pilih Mengikut Kelas</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedClass("all")}
                className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                  selectedClass === "all"
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-xl scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:scale-105'
                }`}
              >
                ✨ Semua ({dailyLogs.length})
              </button>
              {availableClasses.map((className, idx) => {
                // Count daily logs for this class
                const classLogCount = dailyLogs.filter(log => {
                  const child = children.find(c => c.id === log.child_id);
                  return child?.class_name === className;
                }).length;
                return (
                  <button
                    key={className}
                    onClick={() => setSelectedClass(className)}
                    className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                      selectedClass === className
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-xl scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:scale-105'
                    }`}
                  >
                    {['🍼', '🧸', '🎨', '🌈', '📚'][idx % 5]} {className.split('(')[0].trim()} ({classLogCount})
                  </button>
                );
              })}
            </div>
          </AnimatedCard>
        )}

        {/* Alphabet Filter & Statistics - NEW FEATURES */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && (
          <>
            {isModuleOn("filter_name_daily_logs") && (
            <AnimatedCard className="mb-6" delay={350} color="blue">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow-lg">
                  <span className="text-xl">🔤</span>
                </div>
                <h3 className="font-black text-gray-700">Cari Mengikut Nama</h3>
              </div>
              <AlphabetFilter
                onFilterChange={(filter) => {
                  if (filter.type === 'all') {
                    setNameFilter('');
                  } else {
                    setNameFilter(filter.value);
                  }
                }}
              />
            </AnimatedCard>
            )}

            {/* Statistics Summary */}
            {selectedClass !== 'all' && (
              <AnimatedCard className="mb-6" delay={380} color="purple">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
                      <span className="text-xl">📊</span>
                    </div>
                    <h3 className="font-black text-gray-700">Status Kemaskini</h3>
                  </div>
                  <StatisticsSummary
                    className={selectedClass}
                    date={selectedDate}
                    type="daily_log"
                    onOpenModal={(stats) => {
                      setStatsData(stats);
                      setShowStatsModal(true);
                    }}
                  />
                </div>
              </AnimatedCard>
            )}
          </>
        )}

        {/* Daily Logs List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {dailyLogs
              .filter(log => {
                if (selectedClass === "all") return true;
                const child = children.find(c => c.id === log.child_id);
                return child?.class_name === selectedClass;
              })
              .map(log => {
                const child = children.find(c => c.id === log.child_id);
                const isHighlighted = highlightedLogId === log.id;
                return (
                  <div 
                    key={log.id} 
                    ref={isHighlighted ? highlightedCardRef : null}
                  >
                    <DailyLogCard
                      log={log}
                      child={child}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleOpenDuplicate}
                      onViewImage={setViewImageUrl}
                      showActions={user.role === 'admin' || user.role === 'guru'}
                      isHighlighted={isHighlighted}
                      user={user}
                    />
                  </div>
                );
              })}
          </div>
          {dailyLogs.length === 0 && (
            <div data-testid="no-logs-message" className="text-center py-16 px-6">
              {/* Decorative background */}
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 w-28 h-28 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full blur-xl opacity-50"></div>
                <div className="relative w-24 h-24 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto">
                  <FileText className="w-12 h-12 text-blue-400" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">Tiada Log Harian</h3>
              <p className="text-gray-500 text-sm mb-1">Tiada log harian direkodkan untuk tarikh ini</p>
              <p className="text-xs text-gray-400">Cuba pilih tarikh lain atau tunggu guru merekod aktiviti</p>
            </div>
          )}
        </div>

      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          setEditingLog(null);
          setFormData(getDefaultFormData());
          setPhotoFile(null);
          setHealthPhotoFile(null);
          setAddMode("individual");
          setSelectedClassForBulk("");
          setActiveFormTab("saringan");
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{editingLog ? "Edit Log Harian" : "Tambah Log Harian"}</DialogTitle>
          </DialogHeader>
          <DailyLogForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => setShowDialog(false)}
            editingLog={editingLog}
            childrenList={children}
            filteredChildren={filteredChildren}
            addMode={addMode}
            setAddMode={setAddMode}
            selectedClassForBulk={selectedClassForBulk}
            setSelectedClassForBulk={setSelectedClassForBulk}
            availableClasses={availableClasses}
            photoFile={photoFile}
            setPhotoFile={setPhotoFile}
            healthPhotoFile={healthPhotoFile}
            setHealthPhotoFile={setHealthPhotoFile}
            stoolPhotoFile={stoolPhotoFile}
            setStoolPhotoFile={setStoolPhotoFile}
            activeFormTab={activeFormTab}
            setActiveFormTab={setActiveFormTab}
            bulkSubmitting={bulkSubmitting}
            user={user}
            excludedChildIds={excludedChildIds}
            setExcludedChildIds={setExcludedChildIds}
            excludeSearch={excludeSearch}
            setExcludeSearch={setExcludeSearch}
          />
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={!!viewImageUrl} onOpenChange={(open) => {
        if (!open) setViewImageUrl(null);
      }}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader>
            <DialogTitle className="text-center">Gambar Log Harian</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 space-y-4">
            {viewImageUrl && (
              <>
                <img
                  src={optimizeCloudinaryUrl(viewImageUrl)}
                  alt="Daily Log"
                  className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-lg"
                  loading="lazy"
                />
                <Button
                  data-testid="download-dailylog-image"
                  onClick={() => downloadImage(viewImageUrl, "log_harian")}
                  className="bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white font-bold px-6 py-2 rounded-xl shadow-md"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Muat Turun Gambar
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Log Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-green-600" />
              Duplicate Log Harian
            </DialogTitle>
          </DialogHeader>
          
          {duplicateLog && (
            <div className="space-y-4">
              {/* Log Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-gray-800">
                  {children.find(c => c.id === duplicateLog.child_id)?.full_name || 
                   children.find(c => c.id === duplicateLog.child_id)?.name || "Unknown"}
                </p>
                <p className="text-gray-600">Mood: {duplicateLog.mood} • Suhu: {duplicateLog.temperature}°C</p>
                <p className="text-gray-500 text-xs mt-1">
                  {duplicateLog.date}
                </p>
              </div>

              {/* Duplicate Mode */}
              <div className="space-y-3">
                <label className="font-semibold text-sm">Pilih Mod Duplicate:</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDuplicateMode("individual")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "individual"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    🧒 Individu Sama
                  </button>
                  <button
                    onClick={() => setDuplicateMode("multiple")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "multiple"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    👥 Pilih Kanak-kanak
                  </button>
                </div>
              </div>

              {/* Show child selection if multiple mode */}
              {duplicateMode === "multiple" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-sm">Pilih Kanak-kanak:</label>
                    <span className="text-xs text-gray-500">
                      {selectedChildrenForDuplicate.length} dipilih
                    </span>
                  </div>
                  
                  {/* Quick select by class */}
                  <div className="flex flex-wrap gap-1">
                    {availableClasses.map(cls => (
                      <button
                        key={cls}
                        onClick={() => selectAllChildrenForDuplicate(cls)}
                        className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
                      >
                        + {cls.split(' ')[0]}
                      </button>
                    ))}
                  </div>
                  
                  {/* Children list */}
                  <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                    {children.map(child => (
                      <label
                        key={child.id}
                        className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 ${
                          selectedChildrenForDuplicate.includes(child.id) ? "bg-green-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedChildrenForDuplicate.includes(child.id)}
                          onChange={() => toggleChildForDuplicate(child.id)}
                          className="rounded text-green-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {child.full_name || child.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {child.class_name || "Tiada kelas"}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDuplicateDialog(false)}
                  className="flex-1"
                  disabled={duplicating}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleDuplicateLog}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={duplicating || (duplicateMode === "multiple" && selectedChildrenForDuplicate.length === 0)}
                >
                  {duplicating ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Duplicating...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate {duplicateMode === "multiple" ? `(${selectedChildrenForDuplicate.length})` : ""}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        statistics={statsData}
        type="daily_log"
        className={selectedClass}
        onSelectChild={(child) => {
          setShowStatsModal(false);
          setFormData(getDefaultFormData());
          setFormData(prev => ({
            ...prev,
            child_id: child.id,
            date: selectedDate
          }));
          setEditingLog(null);
          setShowDialog(true);
        }}
      />
    </PageLayout>
  );
}
